import { Bugfender } from '@bugfender/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';

import { addDocument } from '@/api/document';

import { useAppDispatch } from '@/hooks/redux';

import { notifyUserChanged } from '@/redux/auth/actions';

import { StepVideoRecorderState } from '@/types/components/verification';
import { DocumentSection, DocumentType } from '@/types/document';

export const useLivenessCheckRecorder = (
  options: {
    minorClientId?: number;
    operationId?: string;
  } = {},
): StepVideoRecorderState => {
  const { minorClientId, operationId } = options;
  const dispatch = useAppDispatch();
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [isVideoUploaded, setIsVideoUploaded] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const recordingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chunks = useRef<Blob[]>([]);
  const uploadPromiseRef = useRef<Promise<void> | null>(null);
  const recordingStartTime = useRef<number | null>(null);

  // Start recording media
  const startRecording = useCallback(async () => {
    try {
      chunks.current = [];
      recordingStartTime.current = Date.now();

      // Clean up existing stream if any
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop());
        videoStreamRef.current = null;
      }

      const tracks = navigator.mediaDevices.getUserMedia({ video: true });
      const stream = (await tracks).clone();
      const videoOptions = {
        mimeType: MediaRecorder.isTypeSupported('video/mp4')
          ? 'video/mp4'
          : 'video/webm',
      };

      // Attach the stream to a video element
      videoStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      mediaRecorderRef.current = new MediaRecorder(stream, videoOptions);
      mediaRecorderRef.current.ondataavailable = event => {
        if (event.data && event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);

      // Reset upload state when starting new recording
      setIsVideoUploaded(false);
      uploadPromiseRef.current = null;
    } catch (err) {
      console.error('Error accessing media devices:', err);
    }
  }, []);

  const uploadRecording = useCallback(
    async (blob: Blob): Promise<void> => {
      const isWebm = blob.type.includes('webm');
      const fileName = isWebm ? 'recording.webm' : 'recording.mp4';
      const file = new File([blob], fileName, {
        type: blob.type,
      });
      const payload = {
        file,
        section: DocumentSection.IDVideo,
        type: DocumentType.VideoAddSelfie,
        clientId: minorClientId,
      };
      try {
        if (operationId) {
          await addDocument({ ...payload, operationId });
        } else {
          await addDocument(payload);
        }
        setIsVideoUploaded(true);
      } catch (e) {
        Bugfender.error('Failed to upload video:', e);
        throw e;
      }
    },
    [minorClientId, operationId],
  );

  const stopRecording = useCallback(
    async (options?: { minimumDuration?: number }): Promise<void> => {
      const recorder = mediaRecorderRef.current;

      if (!recorder) {
        return Promise.resolve();
      }

      if (options?.minimumDuration && recordingStartTime.current) {
        const elapsedTime = Date.now() - recordingStartTime.current;
        const remainingTime = options.minimumDuration - elapsedTime;

        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
      }

      const uploadPromise = new Promise<void>(resolve => {
        recorder.onstop = async () => {
          const blob = new Blob(chunks.current, {
            type: MediaRecorder.isTypeSupported('video/mp4')
              ? 'video/mp4'
              : 'video/webm',
          });

          setRecordedBlob(blob);

          // Clear chunks after creating blob
          chunks.current = [];

          // Stop camera tracks after blob is created to ensures MediaRecorder completes metadata finalization
          if (videoStreamRef.current) {
            videoStreamRef.current.getTracks().forEach(track => track.stop());
            videoStreamRef.current = null;
          }
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }

          if (blob.size > 0) {
            try {
              await uploadRecording(blob);
              dispatch(notifyUserChanged());
              resolve();
            } catch (error) {
              Bugfender.error('Failed to upload recording:', error);
              resolve();
            }
          } else {
            Bugfender.error('Recording blob is empty');
            resolve();
          }

          mediaRecorderRef.current = null;
          setIsRecording(false);
        };

        recorder.stop();
      });

      uploadPromiseRef.current = uploadPromise;

      if (recordingTimeout.current) {
        clearTimeout(recordingTimeout.current);
        recordingTimeout.current = null;
      }

      recordingStartTime.current = null;

      return uploadPromise;
    },
    [uploadRecording, dispatch],
  );

  useEffect(() => {
    if (isRecording && !recordingTimeout.current) {
      recordingTimeout.current = setTimeout(() => {
        void stopRecording();
      }, 20000);
    }

    return () => {
      if (recordingTimeout.current) {
        clearTimeout(recordingTimeout.current);
      }
    };
  }, [isRecording, stopRecording]);

  // Clean up video stream on unmount
  useEffect(() => {
    return () => {
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop());
        videoStreamRef.current = null;
      }
      if (recordingTimeout.current) {
        clearTimeout(recordingTimeout.current);
      }
    };
  }, []);

  return {
    isRecording,
    recordedBlob,
    startRecording,
    stopRecording,
    uploadRecording: () => uploadPromiseRef.current || Promise.resolve(),
    isVideoUploaded,
  };
};
